import React, { useState } from 'react';
import moment, { Moment } from 'moment-timezone';
import { DatePicker } from 'components/UI/V3/DatePicker/DatePicker';
import ExpandableSelector from 'components/UI/V3/ExpandableSelector';
import { useTimeZone } from 'hooks';
import styles from './DateRangeSection.module.scss';

interface DateRangeSectionProps {
    startDate: Date | null;
    endDate: Date | null;
    onChange: (dates: { startDate: Date | null; endDate: Date | null }) => void;
    errors?: { startDate?: string; endDate?: string };
}

export const DateRangeSection: React.FC<DateRangeSectionProps> = ({ startDate, endDate, onChange, errors }) => {
    const timeZone = useTimeZone();
    const [isStartDateExpanded, setIsStartDateExpanded] = useState(false);
    const [isEndDateExpanded, setIsEndDateExpanded] = useState(false);

    const startDateMoment = startDate ? moment.utc(startDate) : null;
    const endDateMoment = endDate ? moment.utc(endDate) : null;

    const handleStartDateChange = (date: Moment | null) => {
        onChange({
            startDate: date ? date.clone().utc().startOf('day').toDate() : null,
            endDate
        });
        setIsStartDateExpanded(false);
    };

    const handleEndDateChange = (date: Moment | null) => {
        onChange({
            startDate,
            endDate: date ? date.clone().utc().endOf('day').toDate() : null
        });
        setIsEndDateExpanded(false);
    };

    return (
        <div className={styles.container}>
            <div className={styles.dateInputsRow}>
                <div className={styles.dateInput}>
                    <ExpandableSelector
                        title="Start Date *"
                        id="start-date-selector"
                        expanded={isStartDateExpanded}
                        childrenClassName={styles.dateSelector}
                        label={startDateMoment?.format('ddd, DD MMM YYYY') || 'Select date'}
                        onExpand={() => setIsStartDateExpanded(!isStartDateExpanded)}
                        onClickAway={() => setIsStartDateExpanded(false)}
                    >
                        <DatePicker
                            minDate={moment().utc().startOf('day')}
                            timeZone={timeZone}
                            value={startDateMoment}
                            onChange={handleStartDateChange}
                        />
                    </ExpandableSelector>
                    {errors?.startDate && <div className={styles.error}>{errors.startDate}</div>}
                </div>

                <div className={styles.dateInput}>
                    <ExpandableSelector
                        title="End Date *"
                        id="end-date-selector"
                        expanded={isEndDateExpanded}
                        childrenClassName={styles.dateSelector}
                        label={endDateMoment?.format('ddd, DD MMM YYYY') || 'Select date'}
                        onExpand={() => setIsEndDateExpanded(!isEndDateExpanded)}
                        onClickAway={() => setIsEndDateExpanded(false)}
                    >
                        <DatePicker
                            minDate={startDateMoment || moment().utc().startOf('day')}
                            timeZone={timeZone}
                            value={endDateMoment}
                            onChange={handleEndDateChange}
                        />
                    </ExpandableSelector>
                    {errors?.endDate && <div className={styles.error}>{errors.endDate}</div>}
                </div>
            </div>
        </div>
    );
};
